import { useState } from 'react'
import { AlertTriangle, FolderX } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ErrorNote } from '@/components/ui/misc'
import { Dialog } from '@/components/ui/overlay'
import { api, ApiError } from '@/lib/api'

/**
 * Confirms removal of an empty directory.
 *
 * Kept apart from DeleteDialog because this deletes no data. HCP's namespace is
 * a real directory tree, so emptying a folder leaves the folder itself behind;
 * this removes that leftover and nothing else. The server refuses outright if
 * anything is still inside, so the dangerous case cannot be reached from here.
 */
export function DeleteFolderDialog({
  bucket,
  prefix,
  name,
  onClose,
  onDeleted,
}: {
  bucket: string
  prefix: string
  name: string
  onClose: () => void
  onDeleted: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notEmpty, setNotEmpty] = useState(false)

  async function confirm() {
    setBusy(true)
    setError(null)
    setNotEmpty(false)
    try {
      await api.deleteDirectory(bucket, prefix)
      onDeleted()
      onClose()
    } catch (err) {
      // The expected refusal, reported as guidance rather than as a failure:
      // the folder still has contents, which the user has to remove first.
      if (err instanceof ApiError && err.code === 'DIRECTORY_NOT_EMPTY') {
        setNotEmpty(true)
      } else {
        setError(err instanceof Error ? err.message : 'Could not delete the folder')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={busy ? () => {} : onClose}
      title="Delete folder"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {notEmpty ? 'Close' : 'Cancel'}
          </Button>
          {!notEmpty && (
            <Button variant="danger" onClick={confirm} loading={busy}>
              Delete folder
            </Button>
          )}
        </>
      }
    >
      {notEmpty ? (
        <div className="flex gap-3 rounded-md border border-border bg-muted/40 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <div className="space-y-1">
            <p className="font-medium">This folder is not empty.</p>
            <p className="text-muted-foreground">
              Delete everything inside it first — including any sub-folders. Files held by a
              retention period cannot be removed until it expires.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="flex items-center gap-2">
            <FolderX className="h-4 w-4 shrink-0 text-danger" />
            <span>
              Remove the empty folder <span className="font-medium">{name}</span>?
            </span>
          </p>
          <p className="text-muted-foreground">
            No file data is deleted. HCP keeps folders after their contents are removed, and this
            clears the one left behind.
          </p>
        </div>
      )}

      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
    </Dialog>
  )
}
